import { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import notificationService from "../services/notificationService";

const WHEN = [
	{ value: "", label: "Off" },
	{ value: "ALWAYS", label: "Every run" },
	{ value: "FAILURES", label: "Only failed runs" },
	{ value: "CHANGES", label: "Only when the result changes (passed ↔ failed)" },
];
const CHANNEL_NAMES = { email: "Email", slack: "Slack", teams: "Teams" };

/**
 * A suite's run notifications (⋮ → Notifications on the Suites page): when to send and
 * where (email, Slack webhook, Teams Workflows webhook). A saved webhook isn't shown
 * again (it's a secret): the field stays empty unless a new one is typed.
 */
export default function SuiteNotificationsDialog({ suite, open, onClose }) {
	const { enqueueSnackbar } = useSnackbar();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);
	const [error, setError] = useState(null);
	const [when, setWhen] = useState("");
	const [emails, setEmails] = useState("");
	const [configured, setConfigured] = useState({ slack: false, teams: false });
	// null = unchanged, "" = remove, else a new URL
	const [slack, setSlack] = useState(null);
	const [teams, setTeams] = useState(null);
	const [testResults, setTestResults] = useState(null);

	const apply = (s) => {
		setWhen(s.when || "");
		setEmails((s.emails || []).join(", "));
		setConfigured({ slack: s.slackConfigured, teams: s.teamsConfigured });
		setSlack(null);
		setTeams(null);
	};

	useEffect(() => {
		if (!open || !suite) return;
		setError(null);
		setTestResults(null);
		setLoading(true);
		notificationService
			.get(suite.id)
			.then(apply)
			.catch((err) => setError(err.response?.data?.message || "Couldn't load the notification settings"))
			.finally(() => setLoading(false));
	}, [open, suite]);

	const save = async () => {
		setSaving(true);
		setError(null);
		try {
			const saved = await notificationService.update(suite.id, {
				when: when || null,
				emails: emails.split(/[\s,;]+/).filter(Boolean),
				slackWebhookUrl: slack,
				teamsWebhookUrl: teams,
			});
			apply(saved);
			enqueueSnackbar("Notification settings saved", { variant: "success" });
			return true;
		} catch (err) {
			setError(err.response?.data?.message || "Couldn't save the notification settings");
			return false;
		} finally {
			setSaving(false);
		}
	};

	const sendTest = async () => {
		if (!(await save())) return;
		setTesting(true);
		setTestResults(null);
		try {
			setTestResults(await notificationService.sendTest(suite.id));
		} catch (err) {
			setError(err.response?.data?.message || "Couldn't send the test notification");
		} finally {
			setTesting(false);
		}
	};

	const webhookField = (name, label, placeholder, value, setValue) => (
		<Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}>
			<TextField
				fullWidth
				size="small"
				type="password"
				autoComplete="off"
				label={label}
				placeholder={configured[name] && value === null ? "Configured: leave empty to keep" : placeholder}
				value={value || ""}
				onChange={(e) => setValue(e.target.value === "" ? (configured[name] ? null : "") : e.target.value)}
				helperText={value === "" && configured[name] ? "Will be removed when you save" : configured[name] ? "Configured" : " "}
				InputLabelProps={{ shrink: true }}
			/>
			{configured[name] && value !== "" && (
				<Button size="small" color="error" sx={{ mt: 0.5 }} onClick={() => setValue("")}>
					Remove
				</Button>
			)}
		</Box>
	);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Notifications · {suite?.name}</DialogTitle>
			<DialogContent dividers>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress />
					</Box>
				) : (
					<>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Hear when a run of this suite finishes, whether it was started by hand, on a schedule or from CI. Runs you stop
							yourself don't notify.
						</Typography>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<FormControl sx={{ mb: 2 }}>
							<FormLabel>Send a notification</FormLabel>
							<RadioGroup value={when} onChange={(e) => setWhen(e.target.value)}>
								{WHEN.map((w) => (
									<FormControlLabel key={w.value} value={w.value} control={<Radio size="small" />} label={w.label} />
								))}
							</RadioGroup>
						</FormControl>
						<TextField
							fullWidth
							size="small"
							label="Email addresses"
							placeholder="qa@example.com, lead@example.com"
							value={emails}
							onChange={(e) => setEmails(e.target.value)}
							helperText="Separate them with commas. Up to 20."
							sx={{ mb: 2 }}
						/>
						{webhookField("slack", "Slack incoming webhook URL", "https://hooks.slack.com/services/…", slack, setSlack)}
						{webhookField("teams", "Teams Workflows webhook URL", "https://…logic.azure.com/workflows/…", teams, setTeams)}
						<Typography variant="caption" color="text.secondary" component="div">
							Slack: create an incoming webhook for the channel. Teams: in the channel, add the workflow “Post to a channel when a
							webhook request is received” and paste its URL.
						</Typography>
						{testResults && (
							<Alert severity={Object.values(testResults).every((r) => r === "sent") ? "success" : "warning"} sx={{ mt: 2 }}>
								{Object.entries(testResults).map(([channel, result]) => (
									<div key={channel}>
										{CHANNEL_NAMES[channel] || channel}: {result}
									</div>
								))}
							</Alert>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={sendTest} disabled={loading || saving || testing}>
					{testing ? "Sending…" : "Save & send test"}
				</Button>
				<Box sx={{ flexGrow: 1 }} />
				<Button onClick={onClose}>Close</Button>
				<Button
					variant="contained"
					disabled={loading || saving}
					onClick={async () => {
						if (await save()) onClose();
					}}
				>
					{saving ? "Saving…" : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
